import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { WidgetsService } from '@services/widgets.service';
import { enumPaymentState } from '@data-layer/enums/payments';
import { enumIsfState } from '@data-layer/enums/isf';
import { CompaniesService } from '@services/companies.service';
import { DocumentsService } from '@services/documents.service';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { IsfRepository } from '@data-layer/repositories';
import { enumDropdownActionIconColor, enumDropdownActionMenuMode } from '@shared/form-wrapper/enums';
import { ManagementIsfModel } from '@data-layer/models/management';
import { IsfModel } from '@data-layer/models/isf';
import { take } from 'rxjs/operators';

@Component({
  selector: 'isf-actions',
  templateUrl: './isf-actions.component.html',
  styleUrls: ['./isf-actions.component.scss'],
})
export class IsfActionsComponent implements OnInit {
  @Input() public isf: IsfModel | ManagementIsfModel;
  @Output() public update: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public duplicate: EventEmitter<IsfModel> = new EventEmitter<IsfModel>();
  @Output() public print: EventEmitter<any> = new EventEmitter<any>();
  @Input() public viewIsf: boolean = true;
  @Input() public showViewIsf: boolean;
  @Input() public showPrint: boolean = false;
  @Input() public showDuplicate: boolean = true;
  @Input() public showDelete: boolean = true;
  @Input() public showComplete: boolean = true;
  @Input() public showContactSupport: boolean = true;
  @Input() public showInList: boolean;
  @Input() public iconColor: enumDropdownActionIconColor = enumDropdownActionIconColor.LIGHT_GRAY;

  public isShowComplete: boolean = true;
  public isShowDuplicate: boolean = true;
  public isShowDelete: boolean = true;
  public isShowContactSupport: boolean = false;
  public isShowCreateEntry: boolean = true;
  public completeText: string;
  public openDeleteModal: boolean = false;
  public showPoaRequiredModal: boolean = false;
  public enumDropdownActionMenuMode: typeof enumDropdownActionMenuMode = enumDropdownActionMenuMode;
  private deletedStates: enumIsfState[] = [enumIsfState.CBP_DELETED, enumIsfState.CBP_DELETE_PENDING, enumIsfState.DELETED];

  constructor(
    private router: Router,
    private isfRepository: IsfRepository,
    private widgetsService: WidgetsService,
    private companiesService: CompaniesService,
    private documentsService: DocumentsService,
  ) {}

  public ngOnInit(): void {
    this.getCompleteText();
    this.getIsShowDuplicate();
  }

  public printPage() {
    this.print.emit();
  }

  public completeIsf() {
    this.router.navigate(['isf', this.isf.referenceNumber]);
  }

  public duplicateIsf() {
    this.documentsService
      .canActWithoutPoaPrompt$()
      .pipe(take(1))
      .subscribe((canProceed: boolean) => {
        if (canProceed) {
          this.performDuplicate();
        } else {
          this.showPoaRequiredModal = true;
        }
      });
  }

  public onPoaModalDiscard(): void {
    this.showPoaRequiredModal = false;
  }

  public onPoaModalGoToPoa(): void {
    const companyId: number = this.companiesService.getCompany()?.id;
    if (!companyId) {
      return;
    }
    this.router.navigate(['/companies', 'account-settings', 'company-view', companyId], {
      queryParams: { isEditViewOpen: true },
      fragment: 'power-of-attorney',
    });
  }

  private performDuplicate(): void {
    this.isfRepository
      .duplicate(this.isf)
      .pipe(take(1))
      .subscribe((res) => {
        this.router
          .navigate(['isf', res.referenceNumber], {
            queryParams: { isDuplicate: true },
          })
          .then(() => {
            this.duplicate.emit(res);
          });
      });
  }

  public createEntry() {
    this.router.navigate(['/entry-summary', 'start'], { queryParams: { isfReferenceNumber: this.isf.referenceNumber } });
  }

  public delete() {
    this.openDeleteModal = false;
    this.isfRepository
      .delete(this.isf.id)
      .pipe(take(1))
      .subscribe(() => {
        this.update.emit(true);
      });
  }

  public contactToSupport() {
    const { referenceNumber } = this.isf;
    this.widgetsService.trackData('Contact team:', { referenceNumber });
    this.widgetsService.openWidget(['isf']);
  }

  public openModal() {
    const companyPaymentType: enumCompanyPaymentType = this.companiesService.getCompany().customsPaymentType;

    if (
      (companyPaymentType === enumCompanyPaymentType.MONTHLY && ![undefined, null].includes(this.isf.paymentState)) ||
      (companyPaymentType === enumCompanyPaymentType.INSTANT &&
        [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.isf.paymentState))
    ) {
      this.openDeleteModal = true;
    } else {
      this.delete();
    }
  }

  public onDecline(): void {
    this.openDeleteModal = false;
  }

  private getCompleteText() {
    switch (this.isf.paymentState) {
      case enumPaymentState.NOT_VALIDATED:
      case enumPaymentState.CANCELED:
      case enumPaymentState.FAILED:
        this.completeText = 'COMPLETE_PAYMENT';
        break;
      default:
        this.completeText = 'VIEW';
        break;
    }

    if (!!this.completeText) {
      this.completeText = `BUTTON.${this.completeText}`;
    }
  }

  private getIsShowDuplicate() {
    let isUpdatedAction = true;
    this.isShowDelete = this.showDelete;
    this.isShowComplete = this.showComplete;
    this.isShowCreateEntry = true; // #4745
    if (this.deletedStates.indexOf(this.isf.state) === -1) {
      switch (this.isf.paymentState) {
        case enumPaymentState.CREATED:
        case enumPaymentState.CONFIRMED:
        case enumPaymentState.PENDING:
          this.isShowDelete = false;
          break;
        case enumPaymentState.NOT_VALIDATED:
        case enumPaymentState.CANCELED:
        case enumPaymentState.FAILED:
          this.isShowContactSupport = this.showContactSupport;
          break;
        case enumPaymentState.MULTI_FAILED:
          this.isShowComplete = false;
          this.isShowDuplicate = false;
          this.isShowDelete = false;
          break;
        default:
          isUpdatedAction = false;
          this.isShowDuplicate = this.showDuplicate;
          break;
      }
    } else {
      isUpdatedAction = false;
    }

    if (isUpdatedAction) {
      return;
    }

    // TODO: check
    switch (this.isf.state) {
      case enumIsfState.DRAFT_CBY_V_SEND:
      case enumIsfState.VALIDATED_AT_PROVIDER:
        this.isShowDuplicate = false;
        this.isShowDelete = false;
        break;
      case enumIsfState.NOT_VALID_AT_PROVIDER:
        this.isShowDuplicate = this.showDuplicate; // only for now (#1437)
        this.isShowContactSupport = this.showContactSupport;
        break;
      case enumIsfState.UNRECOVERABLE:
        this.isShowDuplicate = this.showDuplicate; // only for now (#1437)
        this.isShowContactSupport = this.showContactSupport;
        this.isShowDelete = false;
        break;
      case enumIsfState.CBP_DELETE_PENDING:
      case enumIsfState.CBP_DELETED:
      case enumIsfState.DELETED:
      case enumIsfState.PROVIDER_SEND_PENDING:
      case enumIsfState.ACCEPTED_CBP:
      case enumIsfState.BOND_PENDING:
      case enumIsfState.DRAFT_CBP_PEND:
        this.isShowDuplicate = this.showDuplicate;
        this.isShowDelete = false;
        break;
      default:
        this.isShowDuplicate = this.showDuplicate;
        break;
    }
  }
}
